import { useStoreItem } from '@/features/theme-editor/hooks/useStoreItem';
import { GalleryDataSchema } from '@/features/theme-editor/schema/store';
import { useCustomizationStore } from '@/features/theme-editor/stores/customizationStore';
import { GalleryItem } from '@/features/theme-editor/types/store';
import Columns from '@/features/theme-editor/components/ui/Column';
import { z } from 'zod';
import { Panel, PanelFooter } from '../../Panel';
import { Icon } from '../../ui/Icon';
import { ImageUpload } from '../../ui/ImageUpload';

interface Props {
    id: number;
}

const GalleryDataEditSchema = GalleryDataSchema.extend({
    gallery: z.object({
        images: z.array(z.string()),
    }),
});

export const Gallery = ({ id }: Props) => {
    const item = useStoreItem(id);
    const { updateItem } = useCustomizationStore();

    const validation = GalleryDataEditSchema.safeParse(item?.data ?? { gallery: { images: [] } });
    if (!item || !validation.success) {
        console.error('Gallery data validation failed:', validation.error);
        return null;
    }

    const data = validation.data;
    const { images } = data.gallery;

    const handleDelete = (index: number) => {
        updateItem({
            ...item,
            data: {
                ...data,
                gallery: { ...data.gallery, images: images.filter((_: string, i: number) => i !== index) },
            },
        } as GalleryItem);
    };
    const handleChange = (index: number, src: string) => {
        updateItem({
            ...item,
            data: {
                ...data,
                gallery: {
                    ...data.gallery,
                    images: images.map((image: string, i: number) => (i === index ? src : image)),
                },
            },
        } as GalleryItem);
    };
    const handleAdd = (src: string) => {
        updateItem({
            ...item,
            data: { ...data, gallery: { ...data.gallery, images: [...images, src] } },
        } as GalleryItem);
    };

    return (
        <Panel footer={<PanelFooter id={id} />}>
            <Columns
                multiColumns
                items={images
                    .map((item, index) => (
                        <ImageUpload
                            key={index}
                            width={800}
                            image={item}
                            onDelete={() => handleDelete(index)}
                            onUpdate={newSrc => handleChange(index, newSrc)}
                        />
                    ))
                    .concat(<ImageUpload onAdd={src => handleAdd(src)} />)}
            />

            <div className='border-neutral-subtle mt-6 flex flex-row items-center gap-2 rounded-md border p-3 text-sm'>
                <Icon name='masonry' className='text-[48px]' />
                <div className='flex flex-col'>
                    <span className='font-bold'>Masonry layout</span>
                    <span className='text-theme-fg-secondary font-normal'>
                        Images fill a dynamic grid with even spacing.
                    </span>
                </div>
            </div>
        </Panel>
    );
};
